import { DateTime } from "luxon";

import { type MeetingTimeData } from "./AddEventTimesModal.types";
import { buildMeetingTimeFromParts, getUserTimezone } from "./AddEventTimesModal.utils";

export type RecurrencePreset = "none" | "daily" | "weekly" | "monthly" | "annually" | "weekdays" | "custom";

export type RecurrenceFrequency = "day" | "week" | "month" | "year";

export type RecurrenceEndType = "never" | "on" | "after";

export interface CustomRecurrenceConfig {
  daysOfWeek: Set<number>;
  endDate: DateTime | null;
  endType: RecurrenceEndType;
  frequency: RecurrenceFrequency;
  interval: number;
  occurrenceCount: number;
}

export interface RecurrenceState {
  custom?: CustomRecurrenceConfig;
  preset: RecurrencePreset;
}

export const DEFAULT_OCCURRENCE_COUNT = 13;

/** Safety limit when probing open-ended ("never") recurrence without a year cap. */
const OPEN_ENDED_GENERATION_LIMIT = 5000;

const LOOP_SAFETY_YEARS = 20;

export const getMaxEventDateFromNow = (): DateTime => {
  const tz = getUserTimezone();
  return DateTime.now().setZone(tz).plus({ years: 1 }).startOf("day");
};

export const countEventTimes = (occurrenceCount: number, timeRangeCount: number): number => {
  return occurrenceCount * timeRangeCount;
};

export const createDefaultCustomRecurrence = (startDate: DateTime): CustomRecurrenceConfig => {
  const dayKey = luxonWeekdayToDayKey(startDate.weekday);

  return {
    daysOfWeek: new Set([dayKey]),
    endDate: null,
    endType: "never",
    frequency: "week",
    interval: 1,
    occurrenceCount: DEFAULT_OCCURRENCE_COUNT,
  };
};

export const luxonWeekdayToDayKey = (weekday: number): number => {
  return weekday === 7 ? 0 : weekday;
};

export const dayKeyToLuxonWeekday = (dayKey: number): number => {
  return dayKey === 0 ? 7 : dayKey;
};

export const formatOrdinalDay = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

export const getRecurrenceOptionLabel = (preset: RecurrencePreset, startDate: DateTime | null): string => {
  if (!startDate) {
    switch (preset) {
      case "none":
        return "Does not repeat";
      case "daily":
        return "Daily";
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      case "annually":
        return "Annually";
      case "weekdays":
        return "Every weekday (Monday to Friday)";
      case "custom":
        return "Custom...";
      default:
        return "Does not repeat";
    }
  }

  switch (preset) {
    case "none":
      return "Does not repeat";
    case "daily":
      return "Daily";
    case "weekly":
      return `Weekly on ${startDate.toFormat("cccc")}`;
    case "monthly":
      return `Monthly on the ${formatOrdinalDay(startDate.day)}`;
    case "annually":
      return `Annually on ${startDate.toFormat("MMMM d")}`;
    case "weekdays":
      return "Every weekday (Monday to Friday)";
    case "custom":
      return "Custom...";
    default:
      return "Does not repeat";
  }
};

export const formatCustomRecurrenceSummary = (config: CustomRecurrenceConfig): string => {
  const unitLabels: Record<RecurrenceFrequency, [string, string]> = {
    day: ["day", "days"],
    month: ["month", "months"],
    week: ["week", "weeks"],
    year: ["year", "years"],
  };

  const [singular, plural] = unitLabels[config.frequency];
  const intervalLabel = config.interval === 1 ? singular : plural;
  let summary = `Every ${config.interval} ${intervalLabel}`;

  if (config.frequency === "week" && config.daysOfWeek.size > 0) {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const selectedDayNames = Array.from(config.daysOfWeek)
      .sort((a, b) => a - b)
      .map((day) => dayNames[day])
      .join(", ");
    summary += ` on ${selectedDayNames}`;
  }

  if (config.endType === "on" && config.endDate) {
    summary += `, until ${config.endDate.toFormat("MMM d, yyyy")}`;
  } else if (config.endType === "after") {
    summary += `, ${config.occurrenceCount} times`;
  }

  return summary;
};

export const getRecurrenceDisplayLabel = (recurrence: RecurrenceState, startDate: DateTime | null): string => {
  if (recurrence.preset === "custom" && recurrence.custom) {
    return formatCustomRecurrenceSummary(recurrence.custom);
  }

  return getRecurrenceOptionLabel(recurrence.preset, startDate);
};

export const presetToCustomConfig = (preset: RecurrencePreset, startDate: DateTime): CustomRecurrenceConfig => {
  const dayKey = luxonWeekdayToDayKey(startDate.weekday);
  const base = createDefaultCustomRecurrence(startDate);

  switch (preset) {
    case "none":
      return { ...base, endType: "after", frequency: "day", interval: 1, occurrenceCount: 1 };
    case "daily":
      return { ...base, daysOfWeek: new Set(), frequency: "day", interval: 1 };
    case "weekly":
      return { ...base, daysOfWeek: new Set([dayKey]), frequency: "week", interval: 1 };
    case "monthly":
      return { ...base, daysOfWeek: new Set(), frequency: "month", interval: 1 };
    case "annually":
      return { ...base, daysOfWeek: new Set(), frequency: "year", interval: 1 };
    case "weekdays":
      return {
        ...base,
        daysOfWeek: new Set([1, 2, 3, 4, 5]),
        frequency: "day",
        interval: 1,
      };
    case "custom":
      return base;
    default:
      return { ...base, endType: "after", occurrenceCount: 1 };
  }
};

export const getEffectiveRecurrenceConfig = (
  recurrence: RecurrenceState,
  startDate: DateTime,
): CustomRecurrenceConfig => {
  if (recurrence.preset === "custom" && recurrence.custom) {
    return recurrence.custom;
  }

  return presetToCustomConfig(recurrence.preset, startDate);
};

const getWeekIndex = (date: DateTime, anchor: DateTime): number => {
  return Math.round(date.startOf("week").diff(anchor.startOf("week"), "weeks").weeks);
};

const matchesDayFilter = (date: DateTime, daysOfWeek: Set<number>): boolean => {
  if (daysOfWeek.size === 0) {
    return true;
  }

  return daysOfWeek.has(luxonWeekdayToDayKey(date.weekday));
};

const getGenerationLimit = (config: CustomRecurrenceConfig, yearCap: DateTime | null): number => {
  if (config.endType === "after") {
    return config.occurrenceCount;
  }

  if (yearCap) {
    return OPEN_ENDED_GENERATION_LIMIT;
  }

  return OPEN_ENDED_GENERATION_LIMIT;
};

const mergeEndBoundary = (
  config: CustomRecurrenceConfig,
  anchor: DateTime,
  yearCap: DateTime | null,
): DateTime | null => {
  let userBoundary: DateTime | null = null;

  if (config.endType === "on" && config.endDate) {
    userBoundary = config.endDate.setZone(getUserTimezone()).startOf("day");
  }

  if (yearCap && userBoundary) {
    return userBoundary <= yearCap ? userBoundary : yearCap;
  }

  return yearCap ?? userBoundary;
};

const generateOccurrenceDatesInternal = (
  startDate: DateTime,
  config: CustomRecurrenceConfig,
  yearCap: DateTime | null,
): DateTime[] => {
  const tz = getUserTimezone();
  const anchor = startDate.setZone(tz).startOf("day");
  const limit = getGenerationLimit(config, yearCap);
  const endBoundary = mergeEndBoundary(config, anchor, yearCap);
  const dates: DateTime[] = [];
  const loopSafetyLimit = anchor.plus({ years: LOOP_SAFETY_YEARS }).startOf("day");

  if (config.frequency === "month") {
    let current = anchor;

    while (dates.length < limit) {
      if (endBoundary && current > endBoundary) {
        break;
      }

      if (current > loopSafetyLimit) {
        break;
      }

      dates.push(current);
      current = current.plus({ months: config.interval });
      const targetDay = Math.min(anchor.day, current.daysInMonth ?? anchor.day);
      current = current.set({ day: targetDay });
    }

    return dates;
  }

  if (config.frequency === "year") {
    let current = anchor;

    while (dates.length < limit) {
      if (endBoundary && current > endBoundary) {
        break;
      }

      if (current > loopSafetyLimit) {
        break;
      }

      dates.push(current);
      current = current.plus({ years: config.interval });
    }

    return dates;
  }

  if (config.frequency === "week") {
    let current = anchor;

    while (dates.length < limit && current <= loopSafetyLimit) {
      if (endBoundary && current > endBoundary) {
        break;
      }

      if (current >= anchor && matchesDayFilter(current, config.daysOfWeek)) {
        const weekIndex = getWeekIndex(current, anchor);
        if (weekIndex % config.interval === 0) {
          dates.push(current);
        }
      }

      current = current.plus({ days: 1 });
    }

    return dates;
  }

  let current = anchor;

  while (dates.length < limit && current <= loopSafetyLimit) {
    if (endBoundary && current > endBoundary) {
      break;
    }

    if (current >= anchor && matchesDayFilter(current, config.daysOfWeek)) {
      dates.push(current);
    }

    current = current.plus({ days: config.interval });
  }

  return dates;
};

export interface GenerateOccurrenceDatesOptions {
  timeRangeCount?: number;
}

export interface OccurrenceGenerationResult {
  dates: DateTime[];
  eventTimeCount: number;
  isCapped: boolean;
  requestedEventTimeCount: number;
}

export const EVENT_TIMES_CAP_MESSAGE = "Event times are limited to one year from today.";

export const getEventTimesCapMessage = ({ isCapped }: Pick<OccurrenceGenerationResult, "isCapped">): string | null => {
  if (!isCapped) {
    return null;
  }

  return EVENT_TIMES_CAP_MESSAGE;
};

export const generateOccurrenceDatesWithCapInfo = (
  startDate: DateTime,
  config: CustomRecurrenceConfig,
  options: GenerateOccurrenceDatesOptions = {},
): OccurrenceGenerationResult => {
  const timeRangeCount = options.timeRangeCount ?? 1;
  const yearCap = getMaxEventDateFromNow();
  const uncappedDates = generateOccurrenceDatesInternal(startDate, config, null);
  const cappedDates = generateOccurrenceDatesInternal(startDate, config, yearCap);
  const requestedEventTimeCount = countEventTimes(uncappedDates.length, timeRangeCount);
  const eventTimeCount = countEventTimes(cappedDates.length, timeRangeCount);

  return {
    dates: cappedDates,
    eventTimeCount,
    isCapped: eventTimeCount < requestedEventTimeCount,
    requestedEventTimeCount,
  };
};

export const generateOccurrenceDates = (
  startDate: DateTime,
  config: CustomRecurrenceConfig,
  options: GenerateOccurrenceDatesOptions = {},
): DateTime[] => {
  return generateOccurrenceDatesWithCapInfo(startDate, config, options).dates;
};

export interface NormalizeMeetingTimesOptions {
  validSeriesIds?: ReadonlySet<string>;
}

export const normalizeMeetingTimesForSubmit = (
  meetingTimes: MeetingTimeData[],
  options: NormalizeMeetingTimesOptions = {},
): MeetingTimeData[] => {
  const maxDate = getMaxEventDateFromNow();
  const now = DateTime.now();
  const { validSeriesIds } = options;

  return meetingTimes.flatMap((meetingTime) => {
    const localDate = meetingTime.date.setZone(getUserTimezone()).startOf("day");

    if (localDate > maxDate || localDate < now.startOf("day")) {
      return [];
    }

    if (localDate.hasSame(now, "day") && meetingTime.startTime < now) {
      return [];
    }

    let seriesId = meetingTime.seriesId;
    if (seriesId && validSeriesIds && !validSeriesIds.has(seriesId)) {
      seriesId = undefined;
    }

    return [
      buildMeetingTimeFromParts({
        capacity: meetingTime.capacity,
        date: localDate,
        endTime: meetingTime.endTime,
        seriesId,
        startTime: meetingTime.startTime,
      }),
    ];
  });
};

export const buildMeetingTimesFromRecurrence = ({
  capacity,
  occurrenceDates,
  seriesId,
  timeRanges,
}: {
  capacity: number;
  occurrenceDates: DateTime[];
  seriesId?: string;
  timeRanges: [DateTime, DateTime][];
}): MeetingTimeData[] => {
  const meetingTimes: MeetingTimeData[] = [];

  for (const occurrenceDate of occurrenceDates) {
    const localDate = occurrenceDate.setZone(getUserTimezone());

    for (const [start, end] of timeRanges) {
      meetingTimes.push(
        buildMeetingTimeFromParts({
          capacity,
          date: localDate,
          endTime: end,
          seriesId,
          startTime: start,
        }),
      );
    }
  }

  return meetingTimes;
};
